import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, MapPin } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCity } from "../context/CityContext";
import { useI18n, LANGS } from "../i18n";

export function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { city, delay, vendor, setModalOpen } = useCity();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const { t, lang, setLang } = useI18n();

  return (
    <header className="sticky top-0 z-50 bg-ink/90 backdrop-blur-xl border-b border-zinc-800">
      <button
        data-testid="topbar-city"
        onClick={() => setModalOpen(true)}
        className="w-full bg-neon-green text-black text-[11px] uppercase font-bold py-1.5 px-3 flex items-center justify-center gap-1.5 hover:bg-neon-magenta hover:text-white transition-colors"
      >
        {city ? ((vendor?.is_national || city === "Toute la France") ? <>📦 Livraison partout en France sous 48h · colis discret · paiement par virement</> : <>🛵 Livraison à {city} en ~{delay || "moins de 120"} min · paiement à la remise{vendor?.accepts_card ? " (carte ou espèces)" : " (espèces)"}</>) : <>📍 Choisis ta ville pour voir ton délai de livraison</>}
      </button>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <img src="/stuff-logo.png" alt="STUFF CBD" className="h-11 w-11 object-contain drop-shadow-[0_0_8px_rgba(157,255,0,0.4)] group-hover:drop-shadow-[0_0_12px_rgba(192,38,211,0.6)] transition-all" />
          <span className="font-anton text-2xl tracking-wide text-white">STUFF</span>
          <span className="font-graffiti text-neon-magenta text-lg hidden sm:inline">cbd</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          <Link to="/" data-testid="nav-shop" className="text-zinc-300 hover:text-neon-green uppercase text-sm font-semibold tracking-wide transition-colors">{t("nav.shops")}</Link>
          <Link to="/recrutement" data-testid="nav-recruit" className="border border-neon-magenta text-neon-magenta hover:bg-neon-magenta hover:text-white uppercase text-sm font-bold tracking-wide transition-colors px-3 py-1.5">{t("nav.becomeVendor")}</Link>
          <Link to="/club" data-testid="nav-club" className="text-neon-green hover:text-neon-magenta uppercase text-sm font-semibold tracking-wide transition-colors">{t("nav.club")}</Link>
          <Link to="/fidelite" data-testid="nav-loyalty" className="text-zinc-300 hover:text-neon-green uppercase text-sm font-semibold tracking-wide transition-colors">{t("nav.loyalty")}</Link>
          {user && user.role === "vendor" && (
            <Link to="/vendeur" data-testid="nav-vendor-dash" className="text-neon-green hover:text-white uppercase text-sm font-semibold tracking-wide transition-colors">{t("nav.mySpace")}</Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-dash" className="text-neon-magenta hover:text-white uppercase text-sm font-semibold tracking-wide transition-colors">Admin</Link>
          )}
          {user ? (
            <button data-testid="nav-logout" onClick={() => { logout(); navigate("/"); }} className="text-zinc-400 hover:text-white uppercase text-sm font-semibold transition-colors">{t("nav.logout")}</button>
          ) : (
            <Link to="/login" data-testid="nav-login" className="text-zinc-300 hover:text-neon-green uppercase text-sm font-semibold tracking-wide transition-colors">{t("nav.login")}</Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-city-chip"
            onClick={() => setModalOpen(true)}
            className={`hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase px-2.5 py-1.5 border transition-colors ${city ? "border-neon-green/60 text-neon-green hover:bg-neon-green hover:text-black" : "border-neon-magenta text-neon-magenta hover:bg-neon-magenta hover:text-white"}`}
          >
            <MapPin className="h-3.5 w-3.5" /> {city || "Ta ville"}
          </button>
          <select data-testid="lang-selector" value={lang} onChange={(e) => setLang(e.target.value)} className="bg-ink-surface border border-zinc-700 text-zinc-300 text-xs px-1.5 py-1.5 focus:outline-none focus:border-neon-green cursor-pointer" aria-label="Langue">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.code.toUpperCase()}</option>)}
          </select>
          <Link to="/panier" data-testid="nav-cart" className="relative p-2 hover:bg-ink-hover rounded-full transition-colors">
            <ShoppingBag className="h-5 w-5 text-white" />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 bg-neon-green text-black text-xs font-bold h-5 w-5 flex items-center justify-center rounded-full">{count}</span>
            )}
          </Link>
          <button className="md:hidden p-2" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
            {open ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-ink-surface border-t border-zinc-800 px-4 py-4 flex flex-col gap-4">
          <Link to="/" onClick={() => setOpen(false)} className="text-zinc-200 uppercase text-sm font-semibold">{t("nav.shops")}</Link>
          <Link to="/recrutement" onClick={() => setOpen(false)} className="text-zinc-200 uppercase text-sm font-semibold">{t("nav.becomeVendor")}</Link>
          <Link to="/club" onClick={() => setOpen(false)} className="text-neon-green uppercase text-sm font-semibold">{t("nav.club")}</Link>
          <Link to="/fidelite" onClick={() => setOpen(false)} className="text-zinc-200 uppercase text-sm font-semibold">{t("nav.loyalty")}</Link>
          {user && user.role === "vendor" && <Link to="/vendeur" onClick={() => setOpen(false)} className="text-neon-green uppercase text-sm font-semibold">{t("nav.mySpace")}</Link>}
          {user && user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-neon-magenta uppercase text-sm font-semibold">Admin</Link>}
          {user ? (
            <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left text-zinc-400 uppercase text-sm font-semibold">{t("nav.logout")}</button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="text-zinc-200 uppercase text-sm font-semibold">{t("nav.login")}</Link>
          )}
        </div>
      )}
    </header>
  );
}
